/**
 * chatProgressStream — the browser half of the Chat Analyst's live progress
 * transport (WB2, Lane 1082).
 *
 * Tries POST /api/ai-assistant/stream (newline-delimited JSON events) and
 * FALLS BACK to the plain JSON POST on anything short of a completed stream.
 * The mandatory-fallback rule is the design's spine: a corporate proxy that
 * buffers, a mid-stream disconnect, a malformed line, an old browser without
 * ReadableStream — every one of them must degrade to today's behaviour, never
 * to a hang and never to a blank bubble.
 *
 * Dedicated module by design (chat-analyst plan §0.2): Home.js composes the
 * experience but does not own this transport.
 *
 * Contract with the caller — one result shape for BOTH paths:
 *   { ok: boolean, status: number, data: object|null, errData: object|null,
 *     streamed: boolean }
 * `data` is the exact /api/ai-assistant payload (the stream's final event
 * carries it unchanged); `errData` mirrors the JSON endpoint's parsed error
 * body so `chatErrorMessage({status}, errData)` behaves identically.
 *
 * Double-execution discipline: an in-stream `error` event means the turn RAN
 * and failed — it is surfaced, never retried, because a silent re-run would
 * bill a second model execution for the same failure. Only TRANSPORT failures
 * fall back to the plain POST; for a turn the server already persisted, that
 * fallback replays the stored result (same client_turn_id) rather than paying
 * a second execution.
 */

// Feature-detect memo: the stream URLs seen dark (404/405). Per-session; a
// flag flip mid-session is picked up on the next full reload. Keyed by URL
// (Lane 1322) so a dark sourcing-event stream can never turn off streaming
// for ordinary chat, which always passes one URL.
const darkStreamUrls = new Set();

/** Test-only reset for the module-scoped memo. */
export function _resetStreamAvailabilityForTests() {
  darkStreamUrls.clear();
}

/**
 * Dead-stream watchdog FLOOR. The real ceiling is derived per turn from the
 * server's own `accepted.deadline_seconds` (+20s margin), so raising the
 * server turn deadline never makes the client kill a live slow stream; this
 * constant only covers the window before `accepted` arrives (or servers that
 * predate the field). If it fires, the turn deadline has long passed — the
 * stream is dead, not slow.
 */
export const STREAM_WATCHDOG_MS = 200_000;
const WATCHDOG_MARGIN_MS = 20_000;

async function fallbackPost(fallbackUrl, headers, body) {
  const response = await fetch(fallbackUrl, {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    return { ok: false, status: response.status, data: null, errData, streamed: false };
  }
  const data = await response.json();
  return { ok: true, status: response.status, data, errData: null, streamed: false };
}

/**
 * @param {Object} opts
 * @param {string} opts.streamUrl    POST /api/ai-assistant/stream
 * @param {string} opts.fallbackUrl  POST /api/ai-assistant
 * @param {Object} opts.headers      auth headers (both requests)
 * @param {Object} opts.body         the turn request body (both requests)
 * @param {(event: Object) => void} [opts.onEvent]  called per progress event
 *        ({type, line?, labels?, elapsed_ms?}); exceptions are swallowed —
 *        a broken progress renderer may never break the answer.
 * @param {() => Promise<Object>} [opts.onTruncated]  Lane 1133: when the
 *        stream dies BEFORE a terminal event, called INSTEAD of the plain-POST
 *        retry and its result returned as the turn. For a turn whose result
 *        is durably stored server-side (the workbook accept turn stores a
 *        plan), re-running the model is the wrong recovery — the caller
 *        polls the stored result instead; returning null/undefined asks for
 *        the plain-POST replay after all (the turn finished with nothing
 *        stored). Ordinary chat turns pass nothing and keep the plain-POST
 *        replay exactly as before.
 */
export async function requestChatTurn({ streamUrl, fallbackUrl, headers, body, onEvent, onTruncated }) {
  // Capability guard is on the RESPONSE body below (getReader), not on a
  // global — jsdom and some older WebViews lack a global ReadableStream while
  // still serving readable fetch bodies, and vice versa.
  if (darkStreamUrls.has(streamUrl) || typeof TextDecoder === 'undefined') {
    return fallbackPost(fallbackUrl, headers, body);
  }

  let response;
  try {
    response = await fetch(streamUrl, {
      method: 'POST', headers, body: JSON.stringify(body),
    });
  } catch (transportError) {
    // Network-level failure reaching the stream endpoint: fall back once.
    return fallbackPost(fallbackUrl, headers, body);
  }

  if (response.status === 404 || response.status === 405) {
    // Endpoint dark (flag off, or an older deploy). Remember for the session.
    darkStreamUrls.add(streamUrl);
    return fallbackPost(fallbackUrl, headers, body);
  }
  if (!response.ok || !response.body || typeof response.body.getReader !== 'function') {
    // Pre-turn failure (401 etc.) or a body we cannot stream: the plain POST
    // reproduces the same outcome through today's code path.
    return fallbackPost(fallbackUrl, headers, body);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const startedAt = Date.now();
  let watchdogDeadline = startedAt + STREAM_WATCHDOG_MS;
  let buffered = '';
  let sawFinal = null;
  let sawError = null;

  try {
    for (;;) {
      const remainingMs = watchdogDeadline - Date.now();
      if (remainingMs <= 0) {
        try { reader.cancel(); } catch (ignored) { /* already dead */ }
        break;
      }
      const step = await Promise.race([
        reader.read(),
        new Promise((resolve) => { setTimeout(() => resolve({ timedOut: true }), remainingMs); }),
      ]);
      if (step.timedOut) continue; // loop head re-checks the absolute deadline
      const { done, value } = step;
      if (value) buffered += decoder.decode(value, { stream: true });
      const lines = buffered.split('\n');
      buffered = done ? '' : lines.pop();
      for (const line of lines) {
        if (!line.trim()) continue;
        let event;
        try { event = JSON.parse(line); } catch (malformed) { continue; }
        if (event.type === 'accepted'
            && typeof event.deadline_seconds === 'number'
            && event.deadline_seconds > 0) {
          watchdogDeadline = Math.max(
            watchdogDeadline,
            startedAt + (event.deadline_seconds * 1000) + WATCHDOG_MARGIN_MS,
          );
        }
        if (event.type === 'final') { sawFinal = event.payload; continue; }
        if (event.type === 'error') { sawError = event; continue; }
        if (onEvent) {
          try { onEvent(event); } catch (renderError) { /* progress must not break the answer */ }
        }
      }
      if (done) break;
    }
  } catch (midStreamError) {
    sawFinal = null; // truncated stream — fall through to the fallback below
  }

  if (sawFinal) {
    return { ok: true, status: 200, data: sawFinal, errData: null, streamed: true };
  }
  if (sawError) {
    // The turn ran and failed. Surface it exactly as the JSON endpoint would;
    // never silently re-run a failed model execution.
    return {
      ok: false,
      status: typeof sawError.status === 'number' ? sawError.status : 500,
      data: null,
      errData: sawError.detail != null ? { detail: sawError.detail } : null,
      streamed: true,
    };
  }
  // Transport died before a terminal event (disconnect, buffering proxy that
  // never flushed, watchdog). The plain POST is the retry: a persisted turn
  // replays its stored result under the same client_turn_id. A caller whose
  // turn stores its result durably supplies onTruncated and recovers that
  // stored result instead of re-running the model (Lane 1133; measured
  // 2026-09-02: two of eight staging accept streams were cut mid-turn while
  // the server finished and stored the plan).
  if (typeof onTruncated === 'function') {
    const recovered = await onTruncated();
    // A null/undefined result means "the stored path has nothing to give;
    // replay the finished turn" -- the caller established the turn is
    // complete, so the plain POST returns the persisted answer at once.
    if (recovered != null) return recovered;
  }
  return fallbackPost(fallbackUrl, headers, body);
}
